import React, { Component } from 'react'
import '../styles/GlobalStyle.css'
import { firebase, db } from '../firebase/Firebase'
import { flattenDoc } from '.././Util'
import ColType from '.././Types'
import { Button } from 'antd'
import CustomFooter from '../login/CustomFooter'
import MathText, { shouldRenderMath } from './mathtext'

// ── Image assets (Firebase Storage URLs) ──────────────────────────────
const IMAGES = {
    background: 'https://firebasestorage.googleapis.com/v0/b/education-9d7f3.appspot.com/o/basketball-game-background.png?alt=media&token=43551e87-9c06-46de-927d-1b8ea042980e',
    answer: 'https://firebasestorage.googleapis.com/v0/b/education-9d7f3.appspot.com/o/basketball-game-answer-container.png?alt=media&token=3054d1f6-5335-4c79-b490-2d9d774d39bf',
    question: 'https://firebasestorage.googleapis.com/v0/b/education-9d7f3.appspot.com/o/basketball-game-question-container.png?alt=media&token=28b56c19-863c-4a28-863c-e758604a1487',
    basketball: 'https://firebasestorage.googleapis.com/v0/b/education-9d7f3.appspot.com/o/basketball-game-basketball.png?alt=media&token=7cd39c3e-f69f-48f3-87ce-68f48f4332b8',
}
// ── Tunables (finetune the hoop / ball geometry here) ─────────────────
// HOOP_RISE: how far the ball travels UP from its resting position to reach
// the hoop. The hoop sits ~150px (~20vh) from the top of the page; with the
// ball resting at bottom:8vh this rise lands it near the rim. Adjust to taste.
const HOOP_RISE = '-72vh'
// Helper component: a div with fixed dimensions where the background image
// is rendered as an absolutely-positioned <img> scaled larger than the div,
// centered, and behind the content. The div clips overflow so the oversized
// image doesn't affect layout.
const ImageBox = ({ src, style, children, imgScale = 1.3, imgScaleWidth = 1.0,
    imageScaleHeight = 1.0, objectFit = 'contain', top = '50%', left = '50%', onClick, onTouchEnd }) => {
    let styleObj = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%)`,
        width: `${imgScale * 100}%`,
        height: `${imgScale * 100}%`,
        objectFit: objectFit,
        pointerEvents: 'none',
        zIndex: 0,
    };
    if (top !== '50%') styleObj.top = top;
    if (left !== '50%') styleObj.left = left;
    if (imgScaleWidth !== 1.0) {
        styleObj.width = `${imgScaleWidth * 100}%`;
    }
    if (imageScaleHeight !== 1.0) {
        styleObj.height = `${imageScaleHeight * 100}%`;
    }
    return <div
        style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            ...style,
        }}
        onClick={onClick}
        onTouchEnd={onTouchEnd}
    >
        <img
            src={src}
            alt=''
            draggable={false}
            style={styleObj}
        />
        <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {children}
        </div>
    </div>
}
const PLAYER_COLORS = {
    player1: '#B8432E', // brick red (lightened)
    player2: '#3558B0', // deep blue (lightened)
}
const GOLD_DROP_SHADOW = 'drop-shadow(0 0 4px rgba(255, 200, 0, 1)) drop-shadow(0 0 10px rgba(255, 215, 0, 0.95)) drop-shadow(0 0 20px rgba(255, 215, 0, 0.7)) drop-shadow(0 0 35px rgba(255, 200, 0, 0.5))'
const GREEN_DROP_SHADOW = 'drop-shadow(0 0 3px rgba(46, 125, 50, 1)) drop-shadow(0 0 7px rgba(46, 125, 50, 0.95)) drop-shadow(0 0 15px rgba(46, 125, 50, 0.7)) drop-shadow(0 0 26px rgba(46, 125, 50, 0.5))'
const RED_DROP_SHADOW = 'drop-shadow(0 0 3px rgba(198, 40, 40, 1)) drop-shadow(0 0 7px rgba(198, 40, 40, 0.95)) drop-shadow(0 0 15px rgba(198, 40, 40, 0.7)) drop-shadow(0 0 26px rgba(198, 40, 40, 0.5))'
// CSS keyframes for the basketball shot. Injected via a <style> tag because
// inline styles can't define @keyframes. The ball element re-mounts on each
// shot (keyed by shotCount) so the animation replays every time.
const BALL_KEYFRAMES = `
@keyframes bball-swish {
    0%   { transform: translate(-50%, 0) scale(1); opacity: 1; }
    55%  { transform: translate(-50%, ${HOOP_RISE}) scale(0.55); opacity: 1; }
    74%  { transform: translate(-50%, calc(${HOOP_RISE} + 6vh)) scale(0.45); opacity: 1; }
    100% { transform: translate(-50%, calc(${HOOP_RISE} + 11vh)) scale(0.32); opacity: 0; }
}
@keyframes bball-miss-left {
    0%   { transform: translate(-50%, 0) scale(1); opacity: 1; }
    50%  { transform: translate(calc(-50% - 9vw), ${HOOP_RISE}) scale(0.55); opacity: 1; }
    100% { transform: translate(calc(-50% - 18vw), -6vh) scale(0.7); opacity: 0; }
}
@keyframes bball-miss-right {
    0%   { transform: translate(-50%, 0) scale(1); opacity: 1; }
    50%  { transform: translate(calc(-50% + 9vw), ${HOOP_RISE}) scale(0.55); opacity: 1; }
    100% { transform: translate(calc(-50% + 18vw), -6vh) scale(0.7); opacity: 0; }
}
@keyframes bball-rim-out {
    0%   { transform: translate(-50%, 0) scale(1); opacity: 1; }
    50%  { transform: translate(-50%, ${HOOP_RISE}) scale(0.55); opacity: 1; }
    63%  { transform: translate(calc(-50% + 3vw), calc(${HOOP_RISE} + 3vh)) scale(0.55); opacity: 1; }
    100% { transform: translate(calc(-50% + 13vw), -6vh) scale(0.7); opacity: 0; }
}
@keyframes three-point-pulse {
    0%, 100% { box-shadow: 0 0 14px rgba(245,124,0,0.7); }
    50%      { box-shadow: 0 0 30px rgba(245,124,0,1), 0 0 55px rgba(245,124,0,0.6); }
}
`

const shuffle = (arr) => {
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const t = a[i]; a[i] = a[j]; a[j] = t
    }
    return a
}
const dedupeAnswers = (answers) => {
    const norm = (t) => String(t == null ? '' : t).toLowerCase().trim().replace(/\s+/g, ' ').replace(/[.!?,;:]+$/, '')
    const seen = new Set()
    // Put the correct answer first so it wins any tie
    return [...answers]
        .sort((a, b) => (b.correct ? 1 : 0) - (a.correct ? 1 : 0))
        .filter(a => {
            const k = norm(a.answer)
            if (!k || seen.has(k)) return false
            seen.add(k)
            return true
        })
}
class BasketballGame extends Component {
    state = {
        phase: 'intro',
        gameMode: null,
        currentTurn: 'player1',
        player1Score: 0,
        player2Score: 0,
        winScore: 11,
        currentQuestion: null,
        seenQuestionIndices: [],
        allQuestions: [],
        feedbackMessage: null,
        feedbackCorrect: null,
        showFeedback: false,
        selectedAnswerIdx: null,
        winner: null,
        isThreePointRound: false,
        ballAnimation: null, // 'swish' | 'missLeft' | 'missRight' | 'rimOut'
        shotCount: 0,
        game_data: this.props.gameQuestions,
        displayedAnswers: [],
    }
    renderMath = (text) => shouldRenderMath(this.state.game_data) ? <MathText text={text} /> : text

    componentDidMount() {
        const allQuestions = [
            ...this.state.game_data.problem_solving_questions.map((q, i) => ({
                ...q, sourceIndex: i, category: 'problem_solving'
            })),
            ...this.state.game_data.conceptual_questions.map((q, i) => ({
                ...q, sourceIndex: i, category: 'conceptual'
            })),
        ]
        this.setState({ allQuestions })
        this.startGame('multi')
    }
    componentDidUpdate(prevProps) {
        // If autoPlay flips on while a question is already showing, kick it off
        if (!prevProps.autoPlay && this.props.autoPlay &&
            this.state.phase === 'playing' && !this.state.showFeedback) {
            this.scheduleAutoAnswer()
        }
    }
    componentWillUnmount() {
        if (this.autoAnswerTimeout) clearTimeout(this.autoAnswerTimeout)
    }
    scheduleAutoAnswer = () => {
        if (!this.props.autoPlay) return
        if (this.autoAnswerTimeout) clearTimeout(this.autoAnswerTimeout)
        this.autoAnswerTimeout = setTimeout(() => {
            const { displayedAnswers, showFeedback, phase } = this.state
            if (phase !== 'playing' || !displayedAnswers || displayedAnswers.length === 0 || showFeedback) return
            const answers = displayedAnswers
            const correctIdx = answers.findIndex(a => a.correct)
            const wrongIndices = answers
                .map((_, i) => i)
                .filter(i => !answers[i].correct)
            let chosenIdx
            // 70% of the time pick the correct answer
            if (Math.random() < 0.7 && correctIdx !== -1) {
                chosenIdx = correctIdx
            } else if (wrongIndices.length > 0) {
                chosenIdx = wrongIndices[Math.floor(Math.random() * wrongIndices.length)]
            } else {
                chosenIdx = correctIdx
            }
            this.handleAnswerSelect(answers[chosenIdx], chosenIdx)
        }, 1000) // pause so the question is visible before it auto-answers
    }
    // A "round" is player 1's shot followed by player 2's shot. The three-point
    // status (~33% chance) is decided at the start of a round and applies to BOTH
    // players, so it carries over into player 2's turn before re-rolling.
    pickRandomQuestion = () => {
        const { allQuestions, seenQuestionIndices, gameMode, currentTurn, isThreePointRound } = this.state
        let seen = [...seenQuestionIndices]
        if (seen.length >= allQuestions.length) seen = []
        const unseen = allQuestions.map((_, i) => i).filter(i => !seen.includes(i))
        const randomIdx = unseen[Math.floor(Math.random() * unseen.length)]
        seen.push(randomIdx)
        const question = allQuestions[randomIdx]
        // Player 2 continues the same round player 1 just played; everyone else
        // (player 1's turn, or single player) starts a fresh round.
        const continuingRound = gameMode === 'multi' && currentTurn === 'player2'
        const threePoint = continuingRound ? isThreePointRound : Math.random() < 0.33
        this.setState((prev) => ({
            currentQuestion: question,
            displayedAnswers: shuffle(dedupeAnswers(question.answers)),
            seenQuestionIndices: seen,
            showFeedback: false,
            feedbackMessage: null,
            feedbackCorrect: null,
            selectedAnswerIdx: null,
            isThreePointRound: threePoint,
            ballAnimation: null,
            shotCount: prev.shotCount + 1,
        }), () => this.scheduleAutoAnswer())
    }
    startGame = (mode) => {
        this.setState({
            phase: 'playing',
            gameMode: mode,
            currentTurn: 'player1',
            player1Score: 0,
            player2Score: 0,
            seenQuestionIndices: [],
            winner: null,
            ballAnimation: null,
        }, () => this.pickRandomQuestion())
    }
    handleAnswerSelect = (answer, idx) => {
        if (this.state.showFeedback) return
        const { gameMode, currentTurn, player1Score, player2Score, winScore, isThreePointRound } = this.state
        const isCorrect = answer.correct;

        // report the answer up to Games.js for stats
        if (this.props.onAnswered) this.props.onAnswered(!!isCorrect);

        const points = isThreePointRound ? 3 : 2
        let newP1 = player1Score, newP2 = player2Score, nextTurn = currentTurn
        if (isCorrect) {
            if (currentTurn === 'player1') newP1 += points
            else newP2 += points
        }
        let winner = null
        if (newP1 >= winScore) winner = 'player1'
        else if (newP2 >= winScore) winner = 'player2'
        if (gameMode === 'multi') {
            nextTurn = currentTurn === 'player1' ? 'player2' : 'player1'
        }
        // Make = swish. Miss = randomly off to the left, right, or off the rim.
        const missTypes = ['missLeft', 'missRight', 'rimOut']
        const ballAnimation = isCorrect
            ? 'swish'
            : missTypes[Math.floor(Math.random() * missTypes.length)]
        let feedbackMessage
        if (isCorrect) {
            feedbackMessage = isThreePointRound ? 'Three Pointer! +3' : 'Swish! +2'
        } else {
            feedbackMessage = 'Missed Shot'
        }
        this.setState((prev) => ({
            player1Score: newP1,
            player2Score: newP2,
            showFeedback: true,
            feedbackCorrect: isCorrect,
            feedbackMessage,
            selectedAnswerIdx: idx,
            ballAnimation,
            shotCount: prev.shotCount + 1,
        }))
        setTimeout(() => {
            if (winner) {
                setTimeout(() => {
                    this.setState({ phase: 'victory', winner }, () => {
                        if (this.props.autoPlay && this.props.autoPlayOnComplete) {
                            this.props.autoPlayOnComplete()
                        }
                    })
                }, 250)
            }
            else this.setState({ currentTurn: nextTurn }, () => this.pickRandomQuestion())
        }, 1700)
    }
    resetGame = () => {
        this.setState({
            phase: 'intro', gameMode: null, currentTurn: 'player1',
            player1Score: 0, player2Score: 0, currentQuestion: null,
            seenQuestionIndices: [], showFeedback: false,
            feedbackMessage: null, feedbackCorrect: null,
            selectedAnswerIdx: null, winner: null,
            isThreePointRound: false, ballAnimation: null,
        })
    }
    // ── The basketball that flies toward the hoop ──
    renderBall = () => {
        const { ballAnimation, shotCount, isThreePointRound } = this.state
        const animMap = {
            swish: 'bball-swish',
            missLeft: 'bball-miss-left',
            missRight: 'bball-miss-right',
            rimOut: 'bball-rim-out',
        }
        const animName = ballAnimation ? animMap[ballAnimation] : null
        const ballStyle = {
            position: 'absolute',
            bottom: isThreePointRound ? '3vh' : '6vh',
            left: '50%',
            width: isThreePointRound ? '22.5vh' : '25.5vh',
            transform: 'translateX(-50%)',
            zIndex: 6,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.4))',
            ...(animName ? { animation: `${animName} 1.2s ease-in forwards` } : {}),
        }
        return (
            <img
                key={`ball-${shotCount}-${ballAnimation || 'rest'}`}
                src={IMAGES.basketball}
                alt='basketball'
                draggable={false}
                style={ballStyle}
            />
        )
    }
    renderIntro = () => {
        return (
            <div style={styles.overlay}>
                <ImageBox src={IMAGES.question} style={styles.introCard} imgScale={2.2}>
                    <h1 style={styles.title}>Hoops Quiz</h1>
                    <p style={styles.description}>
                        {this.state.game_data.description}
                    </p>
                </ImageBox>
                <div style={styles.modeButtons}>
                    <ImageBox
                        src={IMAGES.answer}
                        style={styles.modeButton}
                        imgScale={1.84}
                        onClick={() => this.startGame('single')}
                        onTouchEnd={(e) => { e.preventDefault(); this.startGame('single') }}
                    >
                        <div style={styles.modeIcon}>🏀</div>
                        <div style={styles.modeLabel}>Single Player</div>
                    </ImageBox>
                    <ImageBox
                        src={IMAGES.answer}
                        style={styles.modeButton}
                        imgScale={1.84}
                        onClick={() => this.startGame('multi')}
                        onTouchEnd={(e) => { e.preventDefault(); this.startGame('multi') }}
                    >
                        <div style={styles.modeIcon}>⚔️</div>
                        <div style={styles.modeLabel}>Multiplayer</div>
                    </ImageBox>
                </div>
            </div>
        )
    }
    renderScoreboard = () => {
        const { gameMode, player1Score, player2Score, winScore, currentTurn } = this.state
        const p1Active = currentTurn === 'player1'
        const p2Active = currentTurn === 'player2'
        return (
            <div style={styles.scoreboard}>
                <div style={{
                    ...styles.scoreItem,
                    borderColor: PLAYER_COLORS.player1,
                    ...(p1Active ? { filter: GOLD_DROP_SHADOW } : {}),
                }}>
                    <div style={{ ...styles.scoreLabel, color: PLAYER_COLORS.player1 }}>
                        {gameMode === 'single' ? 'You' : 'Player 1'}
                    </div>
                    <div style={styles.scoreValue}>{player1Score} / {winScore}</div>
                    <div style={styles.scoreBar}>
                        <div style={{ ...styles.scoreBarFill, width: `${Math.min(100, (player1Score / winScore) * 100)}%`, backgroundColor: PLAYER_COLORS.player1 }} />
                    </div>
                </div>
                {gameMode === 'multi' && (
                    <div style={{
                        ...styles.scoreItem,
                        borderColor: PLAYER_COLORS.player2,
                        ...(p2Active ? { filter: GOLD_DROP_SHADOW } : {}),
                    }}>
                        <div style={{ ...styles.scoreLabel, color: PLAYER_COLORS.player2 }}>Player 2</div>
                        <div style={styles.scoreValue}>{player2Score} / {winScore}</div>
                        <div style={styles.scoreBar}>
                            <div style={{ ...styles.scoreBarFill, width: `${Math.min(100, (player2Score / winScore) * 100)}%`, backgroundColor: PLAYER_COLORS.player2 }} />
                        </div>
                    </div>
                )}
            </div>
        )
    }
    renderQuestion = () => {
        const { currentQuestion, displayedAnswers, showFeedback, feedbackMessage, feedbackCorrect, gameMode, currentTurn, selectedAnswerIdx } = this.state
        if (!currentQuestion) return null
        let turnLabel
        if (gameMode === 'single') {
            turnLabel = <span>Your Shot</span>
        } else if (currentTurn === 'player1') {
            turnLabel = <span><span style={{ color: PLAYER_COLORS.player1 }}>Player 1</span>'s Shot</span>
        } else {
            turnLabel = <span><span style={{ color: PLAYER_COLORS.player2 }}>Player 2</span>'s Shot</span>
        }
        return (
            <div style={styles.questionArea}>
                <div style={styles.turnRow}>
                    <div style={styles.turnBadge}>{turnLabel}</div>
                </div>
                <ImageBox src={IMAGES.question} style={{ ...styles.questionCard, opacity: showFeedback ? 0 : 1 }} imgScale={2.5}>
                    <div style={styles.questionText}>
                        {this.renderMath(currentQuestion.question)}
                    </div>
                </ImageBox>
                <div style={styles.answersContainer}>
                    {displayedAnswers.map((ans, idx) => {
                        let extraStyle = {}
                        if (showFeedback) {
                            if (ans.correct) {
                                extraStyle = { ...styles.correctAnswer, filter: GREEN_DROP_SHADOW }
                            } else if (idx === selectedAnswerIdx && !ans.correct) {
                                extraStyle = { ...styles.incorrectAnswer, filter: RED_DROP_SHADOW }
                            } else {
                                extraStyle = styles.fadedAnswer
                            }
                        }
                        return (
                            <ImageBox
                                key={idx}
                                src={IMAGES.answer}
                                style={{ ...styles.answerCard, ...extraStyle }}
                                objectFit='initial'
                                imgScaleWidth={1.74}
                                imageScaleHeight={1.38}
                                top={'53%'}
                                onClick={() => this.handleAnswerSelect(ans, idx)}
                                onTouchEnd={(e) => { e.preventDefault(); this.handleAnswerSelect(ans, idx) }}
                            >
                                {showFeedback && ans.correct && (
                                    <div style={styles.feedbackIcon}>✓</div>
                                )}
                                {showFeedback && idx === selectedAnswerIdx && !ans.correct && (
                                    <div style={{ ...styles.feedbackIcon, ...styles.feedbackIconWrong }}>✗</div>
                                )}
                                <div style={styles.answerText}>
                                    {this.renderMath(ans.answer)}
                                </div>
                            </ImageBox>
                        )
                    })}
                </div>
                {/* Feedback banner */}
                {showFeedback && (
                    <div style={{
                        ...styles.feedbackBanner,
                        backgroundColor: feedbackCorrect ? 'rgba(27, 94, 32, 0.92)' : 'rgba(183, 28, 28, 0.92)',
                        boxShadow: feedbackCorrect
                            ? '0 0 18px rgba(46,125,50,0.8)'
                            : '0 0 18px rgba(198,40,40,0.8)',
                    }}>
                        <div style={styles.feedbackText}>{feedbackMessage}</div>
                    </div>
                )}
            </div>
        )
    }
    renderPlaying = () => {
        const { isThreePointRound } = this.state
        return (
            <div style={styles.overlay}>
                {isThreePointRound && (
                    <div style={styles.threePointBadge}>🏀 THREE-POINT ROUND! 🏀</div>
                )}
                {this.renderScoreboard()}
                {this.renderQuestion()}
                {this.renderBall()}
            </div>
        )
    }
    renderVictory = () => {
        const { winner, gameMode, player1Score, player2Score } = this.state
        let victoryText = gameMode === 'single'
            ? 'You Won!'
            : winner === 'player1' ? 'Player 1 Wins!' : 'Player 2 Wins!'
        return (
            <div style={styles.overlay}>
                <ImageBox src={IMAGES.question} style={styles.victoryCard} imgScale={2.2}>
                    <div style={styles.victoryEmoji}>🏆</div>
                    <h1 style={{ ...styles.victoryTitle, color: PLAYER_COLORS[winner] }}>{victoryText}</h1>
                    <p style={styles.victorySubtext}>
                        Final score {player1Score}{gameMode === 'multi' ? ` – ${player2Score}` : ''}
                    </p>
                </ImageBox>
                <div style={{ marginTop: '2vh' }}>
                    <ImageBox
                        src={IMAGES.answer}
                        style={styles.playAgainButton}
                        onClick={() => { this.startGame('multi') }}
                        onTouchEnd={(e) => { e.preventDefault(); this.startGame('multi') }}
                        imgScale={1.84}
                    >
                        <div style={styles.playAgainText}>Play Again</div>
                    </ImageBox>
                </div>
            </div>
        )
    }
    render() {
        const { phase } = this.state
        return (
            <div style={styles.gameContainer}>
                <style>{BALL_KEYFRAMES}</style>
                <img
                    src={IMAGES.background}
                    alt='background'
                    style={styles.backgroundImage}
                />
                {phase === 'intro' && this.renderIntro()}
                {phase === 'playing' && this.renderPlaying()}
                {phase === 'victory' && this.renderVictory()}
            </div>
        )
    }
}
const styles = {
    gameContainer: {
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    backgroundImage: {
        position: 'fixed',
        top: 0, left: 0, zIndex: 0,
        width: '100vw', height: '100vh',
        objectFit: 'cover',
    },
    overlay: {
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    // ── Intro ──
    introCard: {
        width: '46vh',
        height: '32vh',
        marginTop: '12vh',
        marginBottom: '6vh',
        padding: '2vh',
    },
    title: {
        fontSize: '4vh',
        fontWeight: 'bold',
        margin: '0 0 1.5vh 0',
        marginTop: '-2vh',
        color: '#222',
    },
    description: {
        fontSize: '1.8vh',
        lineHeight: 1.6,
        color: '#333',
        margin: 0,
        padding: '0 1vh',
    },
    modeButtons: {
        display: 'flex',
        gap: '10vh',
        justifyContent: 'center',
    },
    modeButton: {
        width: '24vh',
        height: '24vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
    },
    modeIcon: {
        fontSize: '5vh',
        marginBottom: '0.8vh',
    },
    modeLabel: {
        fontSize: '2vh',
        fontWeight: '600',
        color: '#222',
    },
    // ── Scoreboard ──
    scoreboard: {
        position: 'absolute',
        top: '2vh',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '70vh',
        zIndex: 10,
    },
    scoreItem: {
        minWidth: '16vh',
        padding: '1vh 1.6vh',
        borderRadius: '1.2vh',
        border: '0.35vh solid',
        backgroundColor: 'rgba(255,255,255,0.9)',
        boxShadow: '0 0.4vh 1.2vh rgba(0,0,0,0.25)',
        transition: 'filter 0.3s ease',
        textAlign: 'center',
    },
    scoreLabel: {
        fontSize: '1.9vh',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.1vh',
        marginBottom: '0.2vh',
    },
    scoreValue: {
        fontSize: '3vh',
        fontWeight: 'bold',
        color: '#111',
    },
    scoreBar: {
        marginTop: '0.5vh',
        height: '1vh',
        width: '12vh',
        borderRadius: '0.5vh',
        backgroundColor: 'rgba(0,0,0,0.15)',
        overflow: 'hidden',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    scoreBarFill: {
        height: '100%',
        borderRadius: '0.5vh',
        transition: 'width 0.4s ease',
    },
    // ── Question area ──
    questionArea: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        marginTop: '30vh', // sits below the hoop (hoop ~ top 20vh)
    },
    turnRow: {
        position: 'absolute',
        top: '2vh',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 11,
        display: 'flex',
        alignItems: 'center',
        gap: '1.5vh',
    },
    turnBadge: {
        padding: '0.8vh 2vh',
        borderRadius: '3vh',
        backgroundColor: 'rgba(255,255,255,0.92)',
        boxShadow: '0 0.3vh 1vh rgba(0,0,0,0.25)',
        textAlign: 'center',
        fontWeight: '700',
        fontSize: '2vh',
        textTransform: 'uppercase',
        letterSpacing: '0.1vh',
        color: '#222',
    },
    threePointBadge: {
        position: 'absolute',
        top: '11vh',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9,
        padding: '1.4vh 3.5vh',
        borderRadius: '4vh',
        backgroundColor: '#F57C00',
        color: '#fff',
        fontWeight: '900',
        fontSize: '3vh',
        letterSpacing: '0.15vh',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        border: '0.4vh solid #fff',
        animation: 'three-point-pulse 1s ease-in-out infinite',
    },
    questionCard: {
        width: '50vh',
        height: '24vh',
        marginBottom: '6vh',
        transition: 'opacity 0.3s ease',
    },
    questionText: {
        fontSize: '2.8vh',
        fontWeight: '600',
        color: '#222',
        lineHeight: 1.5,
        textAlign: 'center',
        marginTop: '-4vh',
    },
    // ── Answers ──
    answersContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '6vh',
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    answerCard: {
        width: '22vh',
        height: '22vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        transition: 'transform 0.2s ease, filter 0.2s ease, opacity 0.2s ease',
    },
    answerText: {
        fontSize: '2vh',
        color: '#222',
        fontWeight: '600',
        lineHeight: 1.4,
        textAlign: 'center',
    },
    // ── Feedback states on answer cards ──
    correctAnswer: {
        transform: 'scale(1.06)',
    },
    incorrectAnswer: {
        transform: 'scale(0.94)',
    },
    fadedAnswer: {
        opacity: 0.3,
        transform: 'scale(0.94)',
    },
    feedbackIcon: {
        width: '3.5vh',
        height: '3.5vh',
        borderRadius: '50%',
        backgroundColor: '#2E7D32',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2vh',
        fontWeight: 'bold',
        marginBottom: '0.5vh',
    },
    feedbackIconWrong: {
        backgroundColor: '#C62828',
    },
    // ── Feedback banner ──
    feedbackBanner: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        padding: '2vh 4vh',
        borderRadius: '2vh',
        zIndex: 8,
        transition: 'opacity 0.2s ease',
    },
    feedbackText: {
        fontSize: '3.2vh',
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#fff',
    },
    // ── Victory ──
    victoryCard: {
        width: '46vh',
        height: '32vh',
        padding: '3vh',
        marginTop: '20vh',
    },
    victoryEmoji: {
        fontSize: '7vh',
        marginBottom: '1vh',
        marginTop: '-2vh',
    },
    victoryTitle: {
        fontSize: '4vh',
        fontWeight: 'bold',
        margin: '0 0 1vh 0',
        color: '#222',
    },
    victorySubtext: {
        fontSize: '2vh',
        color: '#444',
        margin: '0',
    },
    playAgainButton: {
        width: '24vh',
        height: '24vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
    },
    playAgainText: {
        fontSize: '2.2vh',
        fontWeight: 'bold',
        color: '#222',
    },
}
export default BasketballGame